// src/encounter/lib/care-model-options.ts
//
// [PROD-3206] The care models the physician can assign, from the Workflows definition list. Pure: no fetch.
// The rules are pm-react's (`studio/care-modeling/library`: `toCareModelRows`, `isUnscoped`,
// `enrollableCareModels`, `isAutomationDefinition`), so both apps offer the same care models:
//   · Elsa returns one row per VERSION; collapse on `definitionId` (isLatest first, then the highest version).
//   · Drop automations (`tag: "scrips.automation"` or `variables.__scrips_face = "automation"`).
//   · Offer only PUBLISHED models scoped to an organisation (a null / "empty" tenant can be run by any org).
// Plus the enrolment channel (Workflows #177, Samer 18 Oct decision 3): `scripsCareModel.enrolmentChannel`,
// "reception" | "physicianCarePlan"; missing, blank or unknown = reception. It only MARKS a model; it never hides
// one and never refuses (the server does not refuse either).
import type { WorkflowDefinitionWire } from '@/types/care-model-api';

export type EnrolmentChannel = 'reception' | 'physicianCarePlan';

export interface CareModelOption {
  /** The stable id C2 addresses (never the per-version row id). */
  definitionId: string;
  name: string;
  description: string | null;
  channel: EnrolmentChannel;
}

const AUTOMATION_TAG = 'scrips.automation';
const FACE_KEY = '__scrips_face';

export function isAutomationDefinition(def: WorkflowDefinitionWire | null | undefined): boolean {
  if (!def) return false;
  if (typeof def.tag === 'string' && def.tag.trim().toLowerCase() === AUTOMATION_TAG) return true;
  return def.variables?.data?.[FACE_KEY] === 'automation';
}

/** No organisation: null, blank, or the literal "empty" an Elsa 2.5 tenant accessor writes. */
export function isUnscopedTenant(tenantId: string | null | undefined): boolean {
  const t = (tenantId ?? '').trim().toLowerCase();
  return t === '' || t === 'empty';
}

function isBetter(candidate: WorkflowDefinitionWire, held: WorkflowDefinitionWire): boolean {
  if (candidate.isLatest && !held.isLatest) return true;
  if (held.isLatest && !candidate.isLatest) return false;
  return (candidate.version ?? 0) > (held.version ?? 0);
}

/** One definition per care model: the current version of each, automations and id-less rows dropped. */
export function latestDefinitions(items: readonly WorkflowDefinitionWire[] | null | undefined): WorkflowDefinitionWire[] {
  const best = new Map<string, WorkflowDefinitionWire>();
  for (const item of items ?? []) {
    const key = item.definitionId;
    if (!key || isAutomationDefinition(item)) continue;
    const held = best.get(key);
    if (!held || isBetter(item, held)) best.set(key, item);
  }
  return [...best.values()];
}

/** `scripsCareModel` as an object: the variables' JSON text first, then the customAttributes object. */
function careModelMetadata(def: WorkflowDefinitionWire): Record<string, unknown> | null {
  for (const raw of [def.variables?.data?.scripsCareModel, def.customAttributes?.data?.scripsCareModel]) {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, unknown>;
      } catch {
        // Unreadable metadata reads as the default channel; it never makes a model un-assignable.
      }
    }
  }
  return null;
}

/** Who enrols patients in this model. Case-insensitive; anything else (or nothing) = reception. */
export function readEnrolmentChannel(def: WorkflowDefinitionWire): EnrolmentChannel {
  const value = careModelMetadata(def)?.enrolmentChannel;
  return typeof value === 'string' && value.trim().toLowerCase() === 'physiciancareplan' ? 'physicianCarePlan' : 'reception';
}

/**
 * The options, from the collapsed list row and its single read (`detail`, which carries tenant, tag and
 * variables). A row with no detail is not offered: an unknown tenant is never offered for enrolment.
 */
export function toCareModelOptions(
  rows: readonly WorkflowDefinitionWire[],
  details: ReadonlyMap<string, WorkflowDefinitionWire | null>,
): CareModelOption[] {
  const options: CareModelOption[] = [];
  for (const row of rows) {
    const id = row.definitionId;
    if (!id || !row.isPublished) continue;
    const detail = details.get(id);
    if (!detail || isAutomationDefinition(detail)) continue;
    if (isUnscopedTenant(detail.tenantId ?? row.tenantId)) continue;
    const description = (detail.description ?? row.description ?? '').trim();
    options.push({
      definitionId: id,
      name: row.displayName || row.name || detail.displayName || detail.name || id,
      description: description || null,
      channel: readEnrolmentChannel(detail),
    });
  }
  return options.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}
